if (!customElements.get('coverage-variant-cards')) {
  class CoverageVariantCards extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true });
      } else {
        this.initialize();
      }
    }

    initialize() {
      this.cards = [...this.querySelectorAll('[data-coverage-card]')];
      if (this.cards.length === 0) return;

      this.currency = this.dataset.currency || 'AUD';

      this.cards.forEach((card) => this.populateCard(card));
      this.bindVariantChange();
      this.initTooltips();
      if (this.dataset.standalone !== 'true') this.selectPopularVariant();
    }

    initTooltips() {
      if (this.tipsBound) return;

      const buttons = [...this.querySelectorAll('[data-coverage-tip-toggle]')];
      if (buttons.length === 0) return;
      this.tipsBound = true;

      const hoverable = window.matchMedia('(hover: hover)').matches;
      const panelFor = (button) => document.getElementById(button.getAttribute('aria-controls'));

      const setOpen = (button, open) => {
        const panel = panelFor(button);
        if (!panel) return;
        panel.hidden = !open;
        button.setAttribute('aria-expanded', String(open));
      };

      const closeAll = (except) => {
        buttons.forEach((button) => {
          if (button !== except) setOpen(button, false);
        });
      };

      buttons.forEach((button) => {
        // The icon sits inside the card label, so stop the click from selecting the card.
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const open = hoverable ? true : button.getAttribute('aria-expanded') !== 'true';
          closeAll(button);
          setOpen(button, open);
        });

        if (hoverable) {
          button.addEventListener('mouseenter', () => {
            closeAll(button);
            setOpen(button, true);
          });
          button.addEventListener('mouseleave', () => setOpen(button, false));
        }

        const panel = panelFor(button);
        if (panel) {
          panel.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
          });
        }
      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) closeAll();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeAll();
      });
    }

    bindVariantChange() {
      if (this.bound) return;
      this.picker = this.closest('variant-picker');
      if (!this.picker || !this.dataset.prices) return;

      this.bound = true;
      try {
        this.prices = JSON.parse(this.dataset.prices);
      } catch (error) {
        this.prices = {};
      }

      this.picker.addEventListener('on:variant:change', (event) => {
        this.refresh(event.detail && event.detail.variant);
      });
    }

    getVariants() {
      if (this.variants) return this.variants;

      const dataEl = this.picker && this.picker.querySelector('script[type="application/json"]');
      if (!dataEl) return [];

      try {
        this.variants = JSON.parse(dataEl.textContent).product.variants || [];
      } catch (error) {
        this.variants = [];
      }

      return this.variants;
    }

    refresh(selected) {
      if (!selected) return;
      const variants = this.getVariants();

      this.cards.forEach((card) => {
        const index = Number(card.dataset.optionIndex);
        const value = card.dataset.optionValue;
        const variant = variants.find(
          (item) =>
            item.options[index] === value &&
            item.options.every((option, i) => i === index || option === selected.options[i])
        );
        if (!variant) return;

        if (card.dataset.cardType === 'image') {
          this.updateImage(card, variant);
          return;
        }

        const money = (this.prices && this.prices[variant.id]) || {};
        card.dataset.priceCents = variant.price;
        card.dataset.compareAtCents = variant.compare_at_price || 0;

        const price = card.querySelector('.coverage-variant-card__price');
        if (price && money.price) price.textContent = money.price;

        const compare = card.querySelector('[data-coverage-compare]');
        if (compare) {
          compare.textContent = money.compare || '';
          compare.hidden = !money.compare;
        }

        const badge = card.querySelector('[data-coverage-save]');
        if (badge) badge.hidden = true;

        this.populateCard(card);
      });
    }

    updateImage(card, variant) {
      if (card.dataset.fixedImage) return;

      const src = variant.featured_image && variant.featured_image.src;
      const media = card.querySelector('.coverage-variant-card__media');
      if (!media || !src) return;

      let img = media.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.alt = card.dataset.optionValue;
        img.width = 56;
        img.height = 56;
        media.removeAttribute('style');
        media.appendChild(img);
      }

      const url = new URL(src, window.location.href);
      url.searchParams.set('width', '160');
      img.src = url.toString();
    }

    getVolume(title) {
      const match = String(title).match(/(\d+(?:\.\d+)?)\s*(ml|l)\b/i);
      if (!match) return 0;
      const amount = Number(match[1]);
      return match[2].toLowerCase() === 'l' ? amount * 1000 : amount;
    }

    formatMoney(cents) {
      let formatted;

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
        formatted = window.Shopify.formatMoney(Math.round(cents), window.theme.moneyFormat);
      } else {
        formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cents / 100);
      }

      // Drop the cents when they are 00, for example $20.00 becomes $20.
      return formatted.replace(/[.,]00(?!\d)/, '');
    }

    populateCard(card) {
      if (card.dataset.cardType === 'image') return;

      const volume = this.getVolume(card.dataset.variantTitle);
      const price = Number(card.dataset.priceCents);
      if (!volume || !price) return;

      const vehicles = Math.max(1, Math.floor(volume / 350));
      const count = card.querySelector('[data-coverage-count]');
      if (count) count.textContent = `Covers ${vehicles} ${vehicles === 1 ? 'vehicle' : 'vehicles'}`;

      const unit = card.querySelector('[data-coverage-unit]');
      if (unit) unit.textContent = `${this.formatMoney((price / volume) * 100)} / 100ml`;

      if (card.querySelector('.coverage-variant-card__badge--popular')) return;

      const compareAtCents = Number(card.dataset.compareAtCents);
      if (!compareAtCents || compareAtCents <= price) return;

      const saving = compareAtCents - price;
      const badge = card.querySelector('[data-coverage-save]');

      if (saving > 0 && badge) {
        badge.textContent = `Save ${this.formatMoney(saving)}`;
        badge.hidden = false;
      }
    }

    async selectPopularVariant() {
      if (this.dataset.selectPopular !== 'true' || this.defaultSelected) return;
      this.defaultSelected = true;

      await customElements.whenDefined('variant-picker');

      const position = Number(this.dataset.popularPosition);
      const input = this.querySelector(`[data-coverage-position="${position}"]`);
      if (!input) return;

      requestAnimationFrame(() => {
        if (!this.isConnected || input.disabled) return;
        input.click();
      });
    }
  }

  customElements.define('coverage-variant-cards', CoverageVariantCards);
}